import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, MapPin, Calendar, Clock, Plus, Trash2, Mail } from "lucide-react";

/** Tarifs (phase de test : 0 €) — les prix officiels sont Orchestre 60 € / Balcon 40 € */
export const HAV_PRICES = { orchestre: 0, balcon: 0 } as const;

const seatCategory = z.enum(["orchestre", "balcon"], {
  required_error: "Veuillez choisir une catégorie de place",
});

const guestSchema = z.object({
  firstName: z.string().trim().min(2, "Prénom requis").max(100),
  lastName: z.string().trim().min(2, "Nom requis").max(100),
  phone: z.string().trim().min(6, "Téléphone requis").max(30),
  email: z.string().trim().email("Adresse email invalide").max(255),
});

const havSchema = z.object({
  firstName: z.string().trim().min(2, "Prénom requis").max(100),
  lastName: z.string().trim().min(2, "Nom requis").max(100),
  phone: z.string().trim().min(6, "Téléphone requis").max(30),
  email: z.string().trim().email("Adresse email invalide").max(255),
  sponsorName: z.string().trim().min(3, "Nom et prénom du parrain requis").max(200),
  seatCategory,
  additionalDonation: z.coerce
    .number({ invalid_type_error: "Montant invalide" })
    .int("Montant invalide")
    .min(0, "Le don doit être positif")
    .max(1000000)
    .optional()
    .default(0),
  guests: z.array(guestSchema).max(20),
});

type HavFormValues = z.infer<typeof havSchema>;

const CATEGORY_LABEL: Record<"orchestre" | "balcon", string> = {
  orchestre: "Orchestre — 60 €",
  balcon: "Balcon — 40 €",
};

export default function HautVoixInscription() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<HavFormValues>({
    resolver: zodResolver(havSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      sponsorName: "",
      seatCategory: undefined,
      additionalDonation: 0,
      guests: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "guests",
  });

  const values = form.watch();
  const seatsCount = 1 + (values.guests?.length || 0);
  const donation = Number(values.additionalDonation) || 0;
  const seatsTotal =
    (values.seatCategory ? HAV_PRICES[values.seatCategory] : 0) * seatsCount;
  const total = seatsTotal + donation;
  const seats = 1 + (values.guests?.length || 0);

  const onSubmit = async (data: HavFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: inserted, error } = await supabase.rpc("insert_hav_registration", {
        p_first_name: data.firstName,
        p_last_name: data.lastName,
        p_email: data.email,
        p_phone: data.phone,
        p_sponsor_name: data.sponsorName,
        p_seat_category: data.seatCategory,
        p_additional_donation: data.additionalDonation || 0,
        p_guests: data.guests.map((g) => ({
          first_name: g.firstName,
          last_name: g.lastName,
          email: g.email,
          phone: g.phone,
          seat_category: data.seatCategory,
        })),
      } as any);

      if (error) throw error;

      const reg = (inserted as any)?.[0] || inserted;

      const emailResult = await supabase.functions.invoke("send-hav-verification", {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          registrationId: reg.id,
          verificationToken: reg.verification_token,
          amount: reg.amount,
          seatsCount: reg.seats_count,
          seatCategory: data.seatCategory,
        },
      });

      if (emailResult.error) console.error("Email error:", emailResult.error);

      setIsSubmitted(true);
    } catch (err: any) {
      console.error("HAV registration error:", err);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Une erreur est survenue lors de la préinscription. Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF200] relative overflow-hidden">
      {/* Bandes diagonales façon affiche */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-24 h-[140%] w-40 rotate-[35deg] bg-[#00C2F3]" />
        <div className="absolute -top-40 right-24 h-[140%] w-16 rotate-[35deg] bg-[#00C2F3]/70" />
        <div className="absolute top-1/3 -left-32 h-24 w-96 rotate-[35deg] bg-white/80" />
      </div>

      <main className="relative z-10 container mx-auto px-4 py-10 md:py-14">
        {/* Titre */}
        <header className="max-w-3xl">
          <p className="text-[#00A6DB] font-extrabold italic tracking-widest text-sm md:text-base uppercase">
            Soirée caritative
          </p>
          <h1 className="mt-2 text-4xl md:text-6xl font-extrabold italic text-[#0A1A6B] uppercase leading-none">
            À Haute Voix
          </h1>
          <p className="text-[#0A1A6B] font-extrabold italic uppercase text-lg md:text-2xl">
            Mode d'emploi d'un futur commun
          </p>
        </header>

        {/* Infos pratiques */}
        <div className="mt-6 flex flex-wrap gap-4 md:gap-8 text-[#0A1A6B] font-semibold">
          <span className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#E5007E]" /> Jeudi 5 novembre 2026
          </span>
          <span className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#E5007E]" /> 19h30 – 22h
          </span>
          <span className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#E5007E]" /> Théâtre de la Tour Eiffel — 4 Square Rapp, Paris 7ᵉ
          </span>
        </div>

        {/* Formulaire */}
        <div className="mt-8 max-w-3xl">
          <Card className="border-0 shadow-2xl bg-white/95">
            <CardContent className="pt-6 pb-8">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#FFF200] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-[#0A1A6B]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#0A1A6B] mb-2">
                    Préinscription enregistrée — action requise !
                  </h2>
                  <div className="rounded-lg border-2 border-[#0A1A6B] bg-[#FFF200] p-5 my-4 text-center">
                    <p className="font-bold text-[#0A1A6B] text-lg mb-1">
                      Vérifiez votre boîte mail et procédez au paiement pour confirmer votre préinscription.
                    </p>
                    <p className="text-[#0A1A6B] font-medium">
                      Sans cette étape, votre place ne sera pas réservée.
                    </p>
                  </div>
                </div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div>
                      <h2 className="text-xl font-bold text-[#0A1A6B]">
                        Participant principal
                      </h2>
                      <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Prénom *</FormLabel>
                              <FormControl>
                                <Input placeholder="Prénom" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nom *</FormLabel>
                              <FormControl>
                                <Input placeholder="Nom" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Téléphone *</FormLabel>
                              <FormControl>
                                <Input type="tel" placeholder="06 12 34 56 78" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email *</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="votre@email.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="sponsorName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nom et prénom du parrain *</FormLabel>
                              <FormControl>
                                <Input placeholder="Ex : Marie Dupont" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="seatCategory"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Catégorie de place *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Choisir une catégorie" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="orchestre">{CATEGORY_LABEL.orchestre}</SelectItem>
                                  <SelectItem value="balcon">{CATEGORY_LABEL.balcon}</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Accompagnants */}
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-[#0A1A6B]">
                          Autres participants
                        </h2>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            append({
                              firstName: "",
                              lastName: "",
                              phone: "",
                              email: "",
                            })
                          }
                        >
                          <Plus className="h-4 w-4 mr-1" /> Ajouter une personne
                        </Button>
                      </div>

                      {fields.length === 0 && (
                        <p className="mt-2 text-sm text-muted-foreground">
                          Vous pouvez inscrire d'autres personnes ; tous les champs sont alors obligatoires.
                        </p>
                      )}

                      <div className="mt-4 space-y-6">
                        {fields.map((f, index) => (
                          <div key={f.id} className="rounded-lg border p-4 bg-muted/30">
                            <div className="flex items-center justify-between mb-3">
                              <p className="font-semibold text-[#0A1A6B]">
                                Participant {index + 2}
                              </p>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => remove(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <FormField
                                control={form.control}
                                name={`guests.${index}.firstName`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Prénom *</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Prénom" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`guests.${index}.lastName`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Nom *</FormLabel>
                                    <FormControl>
                                      <Input placeholder="Nom" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`guests.${index}.phone`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Téléphone *</FormLabel>
                                    <FormControl>
                                      <Input type="tel" placeholder="06 12 34 56 78" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`guests.${index}.email`}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Email *</FormLabel>
                                    <FormControl>
                                      <Input type="email" placeholder="email@exemple.com" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <p className="sm:col-span-2 text-xs text-muted-foreground">
                                Catégorie de place identique à celle du participant principal.
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Don supplémentaire */}
                    <div className="rounded-lg border border-[#0A1A6B]/20 bg-white p-4">
                      <FormField
                        control={form.control}
                        name="additionalDonation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-[#0A1A6B] font-bold">
                              Je souhaite ajouter un don supplémentaire (optionnel)
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                placeholder="0"
                                {...field}
                                value={field.value ?? 0}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value === "" ? 0 : Number(e.target.value)
                                  )
                                }
                              />
                            </FormControl>
                            <p className="text-xs text-muted-foreground">
                              Ce don s'ajoute au prix des places et ouvre droit au reçu fiscal.
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Récapitulatif */}
                    <div className="rounded-lg bg-[#FFF200]/60 p-4 text-center">
                      <p className="text-[#0A1A6B] font-semibold">
                        {seats} place{seats > 1 ? "s" : ""}
                        {donation > 0 ? ` + don de ${donation} €` : ""} — Total à régler :{" "}
                        <span className="text-xl font-bold">{total} €</span>
                      </p>
                      <p className="text-xs text-[#0A1A6B]/80 mt-1">
                        Un reçu fiscal (CERFA) du montant total vous sera adressé par email suite
                        à votre paiement et à la validation de l'inscription par le Bureau de
                        l'association.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-6 bg-[#0A1A6B] hover:bg-[#06124d] text-white text-lg font-semibold"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        "Je me préinscris"
                      )}
                    </Button>
                    <div className="bg-[#FFFEC4] border border-[#0A1A6B]/20 rounded-lg p-4 text-center">
                      <p className="text-sm font-semibold text-[#0A1A6B] leading-relaxed">
                        Votre inscription deviendra définitive après réception du paiement et
                        confirmation par le bureau de l'association.<br />
                        Si votre inscription ne pouvait être retenue, votre paiement vous serait
                        intégralement remboursé.
                      </p>
                    </div>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Bandeau rose bas de page */}
      <footer className="relative z-10 bg-[#F9A8DC] py-6 text-center text-[#0A1A6B] font-semibold">
        <p>Places limitées (400) - Pour toute information merci de nous envoyer un mail à contact@ledlr.fr</p>
      </footer>
    </div>
  );
}
